
import { Article as ReaderArticle } from "@/components/reader-home/types";
import { Article as JournalistArticle } from "@/hooks/journalist/types";
import { Article as DatabaseArticle } from "@/types/models";

/**
 * Transforms a database article object into a standardized format for the UI
 */
export const transformDatabaseArticleToUiArticle = (
  article: Partial<DatabaseArticle> & { author?: any, tags?: string[] }
): ReaderArticle => {
  // Extract author information
  const authorName = article.author?.display_name || 
                     article.author?.name || 
                     'Unknown Author';
  
  const authorAvatar = article.author?.avatar_url || 
                       article.author?.avatar || 
                       `https://ui-avatars.com/api/?name=${encodeURIComponent(authorName)}&background=random`;
  
  // Determine the read time (with fallbacks)
  const readTime = article.reading_time || 
                  article.read_time || 
                  Math.floor(5 + Math.random() * 5);
  
  // Format the date
  const date = article.published_at ? 
              new Date(article.published_at).toLocaleDateString() : 
              new Date(article.created_at || Date.now()).toLocaleDateString();
  
  // Get the image URL with fallbacks, prioritizing featured_image_url, then header_image_url
  const imageUrl = article.featured_image_url || 
                  article.header_image_url || 
                  article.cover_image || 
                  'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=500&h=350&auto=format&fit=crop';
  
  // Transform to standard reader article format
  return {
    id: article.id || '',
    title: article.title || 'Untitled Article',
    author: authorName,
    author_avatar: authorAvatar,
    authorId: article.author_id || '',
    date: date,
    image: imageUrl,
    excerpt: article.excerpt || '',
    readTime: typeof readTime === 'number' ? readTime.toString() : readTime,
    topics: article.tags || [],
    hasAudio: !!article.has_audio,
    lead: article.lead || '',
    featured: article.status === 'published'
  };
};

/**
 * Transforms a reader article to journalist article format
 */
export const transformToJournalistArticle = (article: ReaderArticle): JournalistArticle => {
  return {
    id: article.id,
    title: article.title,
    excerpt: article.excerpt,
    lead: article.lead,
    date: article.date,
    readTime: typeof article.readTime === 'string' ? 
              parseInt(article.readTime.replace('min read', '').trim()) : 
              article.readTime || 3,
    category: article.topics?.[0] || 'General',
    featured: article.featured || false,
    premium: false,
    image: article.image,
    author_id: article.authorId,
    author: article.author,
    author_name: article.author,
    author_avatar: article.author_avatar
  };
};
